import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Table,
  DatePicker,
  Space,
  Typography,
  Empty
} from 'antd';
import {
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/admin/governanceService';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDate } from '@utils/dateTime';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const UsageRecords = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await governanceService.getUsageSummary();
      if (response.success) {
        setSummary(response.data.summary || response.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage summary:', error);
    }
  }, []);

  const fetchRecords = useCallback(async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await governanceService.getUsageRecords({
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      });

      if (response.success) {
        setRecords(response.data.usage_records || response.data.records || []);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch usage records:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSummary();
    fetchRecords(dateRange[0], dateRange[1]);
  }, [fetchSummary, fetchRecords, dateRange]);

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    }
  };

  const columns = [
    {
      title: t('admin.ai_center.usage_records.date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date)
    },
    {
      title: t('admin.ai_center.usage_records.type'),
      dataIndex: 'usage_type',
      key: 'usage_type'
    },
    {
      title: t('admin.ai_center.usage_records.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right'
    },
    {
      title: t('admin.ai_center.usage_records.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      align: 'right',
      render: (price, record) => price != null ? `${record.currency} ${price.toLocaleString()}` : '-'
    },
    {
      title: t('admin.ai_center.usage_records.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount, record) => (
        <Text strong>{record.currency} {amount?.toLocaleString()}</Text>
      )
    }
  ];

  const usagePercent = summary?.total_purchased > 0
    ? Math.round((summary.total_consumed / summary.total_purchased) * 100)
    : 0;

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader items={['ai_center', 'ai_center.usage_records']} basePath="/admin" />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Summary Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title={t('admin.ai_center.usage_records.balance')}
                  value={summary?.balance ?? 0}
                  precision={2}
                  prefix={summary?.currency}
                  valueStyle={{ color: summary?.balance > 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title={t('admin.ai_center.usage_records.total_consumed')}
                  value={summary?.total_consumed ?? 0}
                  precision={2}
                  prefix={summary?.currency}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title={t('admin.ai_center.usage_records.remaining')}
                  value={summary?.remaining ?? summary?.balance ?? 0}
                  precision={2}
                  prefix={summary?.currency}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title={t('admin.ai_center.usage_records.usage_ratio')}
                  value={usagePercent}
                  suffix="%"
                  valueStyle={{ color: usagePercent > 90 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
          </Row>

          {/* Records Table */}
          <Card
            title={t('admin.ai_center.usage_records.records_title')}
            extra={
              <Space>
                <Text>{t('admin.ai_center.usage_records.date_range')}:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateChange}
                  allowClear={false}
                />
              </Space>
            }
          >
            {loading ? (
              <div style={{ padding: 60, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : records.length > 0 ? (
              <Table
                dataSource={records}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => t('common.pagination.total', { total })
                }}
                size="small"
              />
            ) : (
              <Empty description={t('admin.ai_center.usage_records.no_records')} />
            )}
          </Card>
        </Space>
      </div>
    </div>
  );
};

export default UsageRecords;
